'use client';

import type { ComponentProps, SyntheticEvent } from 'react';
import { useRef } from 'react';

type SharedVideoProps = Omit<ComponentProps<'video'>, 'src' | 'controls' | 'className'> & {
  url: string;
  caption: string;
  className: string;
  /** False where the page draws its own controls around the video. */
  controls?: boolean;
  /** Called when the video cannot play: an error a fresh link cannot fix, or a fresh link that failed too. */
  onUnplayable?: () => void;
};

/**
 * A shared video that keeps playing past its read link. The route redirects to a link that lasts
 * five minutes, and the browser fetches the rest of the video from that link, so playing or seeking
 * after it expires fails. On that failure the video loads again through the route, which hands out
 * a fresh link, and carries on from where it stopped.
 */
export function SharedVideo({
  url,
  caption,
  className,
  controls = true,
  onPlay,
  onPause,
  onLoadedMetadata,
  onError,
  onUnplayable,
  ...videoProps
}: SharedVideoProps) {
  // Whether the viewer's last press was play rather than pause.
  const wantsPlayback = useRef(false);
  // Where to carry on once the reloaded video's metadata arrives. Set until then, so a route that
  // keeps failing is asked once.
  const pendingPosition = useRef<number | null>(null);

  function reloadAfterExpiredLink(event: SyntheticEvent<HTMLVideoElement>) {
    const video = event.currentTarget;
    if (video.error?.code !== MediaError.MEDIA_ERR_NETWORK || pendingPosition.current !== null) {
      onUnplayable?.();
      return;
    }
    pendingPosition.current = video.currentTime;
    video.load();
  }

  function carryOnAfterReload(event: SyntheticEvent<HTMLVideoElement>) {
    const position = pendingPosition.current;
    if (position === null) {
      return;
    }
    pendingPosition.current = null;
    const video = event.currentTarget;
    video.currentTime = position;
    // Read now, so a pause pressed while the fresh link loads keeps the video paused.
    if (wantsPlayback.current) {
      // A refusal leaves the video paused where it was, with its play button.
      video.play().catch(() => undefined);
    }
  }

  return (
    <video
      {...videoProps}
      src={url}
      controls={controls}
      playsInline
      preload="metadata"
      aria-label={caption}
      className={className}
      onPlay={(event) => {
        wantsPlayback.current = true;
        onPlay?.(event);
      }}
      onPause={(event) => {
        // A browser may pause a video whose download just failed; that is not the viewer's press.
        if (event.currentTarget.error?.code !== MediaError.MEDIA_ERR_NETWORK) {
          wantsPlayback.current = false;
        }
        onPause?.(event);
      }}
      onLoadedMetadata={(event) => {
        carryOnAfterReload(event);
        onLoadedMetadata?.(event);
      }}
      onError={(event) => {
        reloadAfterExpiredLink(event);
        onError?.(event);
      }}
    />
  );
}
